import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

import {
  LOCK_STATUS_COLOR,
  LOCK_STATUS_LABEL,
  type LockPublicView,
} from '@apartsales/shared';

import { ApiCallError, lockApi } from '~/lib/api';

export const Route = createFileRoute('/lock/$code')({
  component: LockStatusPage,
});

function LockStatusPage() {
  const { code } = useParams({ from: '/lock/$code' });
  const codeUpper = code.toUpperCase();

  const { data, error, isLoading, refetch, isFetching } = useQuery<
    LockPublicView,
    ApiCallError
  >({
    queryKey: ['lock', codeUpper],
    queryFn: () => lockApi.getByCode(codeUpper),
    refetchInterval: 30_000, // live status updates every 30s
    refetchOnWindowFocus: true,
    retry: (failureCount, err) =>
      err.code === 'not_found' || err.code === 'invalid_code_format'
        ? false
        : failureCount < 2,
  });

  return (
    <div className="relative min-h-screen overflow-hidden bg-bg text-label">
      {/* Atmospheric backdrop — Apartsales blue glow */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute left-1/2 top-[-10%] h-[700px] w-[700px] -translate-x-1/2 rounded-full blur-[140px]"
          style={{ backgroundColor: 'rgba(42,150,251,0.18)' }}
        />
        <div
          className="absolute bottom-[-20%] right-[-10%] h-[500px] w-[500px] rounded-full blur-[140px]"
          style={{ backgroundColor: 'rgba(42,150,251,0.10)' }}
        />
      </div>

      <main className="mx-auto flex min-h-screen max-w-xl flex-col px-5 py-10">
        <Link
          to="/"
          className="mb-10 inline-flex w-fit items-center gap-2 text-sm text-label-2 transition-colors hover:text-label"
        >
          <span aria-hidden>←</span> apartsales.com
        </Link>

        {isLoading && <SkeletonCard />}

        {error && <ErrorCard error={error} code={codeUpper} onRetry={() => refetch()} />}

        {data && (
          <BoardingPass lock={data} refreshing={isFetching} onRefresh={() => refetch()} />
        )}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Boarding pass — the main artifact users see
// ---------------------------------------------------------------------------

function BoardingPass({
  lock,
  refreshing,
  onRefresh,
}: {
  lock: LockPublicView;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  const statusColor = LOCK_STATUS_COLOR[lock.status];
  const statusLabel = LOCK_STATUS_LABEL[lock.status];

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
      className="overflow-hidden rounded-[28px] border backdrop-blur-2xl"
      style={{
        backgroundColor: 'rgba(255,255,255,0.04)',
        borderColor: 'rgba(255,255,255,0.10)',
        boxShadow:
          '0 1px 0 rgba(255,255,255,0.06) inset, 0 30px 80px -20px rgba(0,0,0,0.6)',
      }}
    >
      {/* Top stripe — colored by status */}
      <div
        className="relative h-1"
        style={{
          background: `linear-gradient(90deg, ${statusColor}, transparent)`,
        }}
      />

      {/* Header — like an airline ticket header */}
      <div className="flex items-start justify-between gap-4 px-7 pb-2 pt-7">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-label-3">
            Apartsales Lock
          </p>
          <p className="mt-1 text-xs text-label-2">
            Фиксация лида · юридический + поведенческий контур
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          aria-label="Обновить"
          className="rounded-full px-3 py-1.5 text-[11px] text-label-2 transition-colors hover:text-label"
          style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
        >
          {refreshing ? 'Обновление…' : 'Обновить'}
        </button>
      </div>

      {/* Code — boarding-pass style mono */}
      <div className="px-7 pb-6 pt-2">
        <p className="font-mono text-[34px] font-semibold tracking-[0.04em] text-label sm:text-[40px]">
          {lock.code}
        </p>
      </div>

      {/* Status pill */}
      <div className="px-7 pb-6">
        <div
          className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium"
          style={{
            backgroundColor: `${statusColor}1f`,
            color: statusColor,
            border: `0.5px solid ${statusColor}55`,
          }}
        >
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: statusColor }}
          />
          {statusLabel}
        </div>
      </div>

      {/* Perforated divider */}
      <Perforation />

      {/* Body — facts grid */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-5 px-7 py-6">
        <Fact label="Покупатель" value={lock.buyer_name_masked ?? '—'} />
        <Fact
          label="Цена фиксации"
          value={formatRubles(lock.agreed_price)}
          mono
        />
        <Fact
          label="Создан"
          value={formatDate(lock.created_at)}
          sub={formatTime(lock.created_at)}
        />
        <Fact
          label="Действует до"
          value={formatDate(lock.expires_at)}
          sub={<Countdown to={lock.expires_at} />}
        />
        {lock.sms_verified_at && (
          <Fact
            label="Подтверждён"
            value={formatDate(lock.sms_verified_at)}
            sub={formatTime(lock.sms_verified_at)}
          />
        )}
        {lock.visit_scheduled_at && (
          <Fact
            label="Показ запланирован"
            value={formatDate(lock.visit_scheduled_at)}
            sub={formatTime(lock.visit_scheduled_at)}
          />
        )}
      </div>

      {/* Footer — IDs */}
      <div
        className="flex items-center justify-between gap-4 px-7 py-4 text-[11px] text-label-3"
        style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
      >
        <span className="font-mono">unit · {lock.unit_id.slice(0, 8)}…</span>
        <span className="font-mono">project · {lock.project_id.slice(0, 8)}…</span>
      </div>
    </motion.article>
  );
}

function Perforation() {
  // Two semi-circular notches (left + right) plus a dashed line — gives the
  // unmistakable feeling of a torn ticket.
  return (
    <div className="relative" aria-hidden>
      <div
        className="absolute -left-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full"
        style={{ backgroundColor: 'rgb(0,0,0)' }}
      />
      <div
        className="absolute -right-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full"
        style={{ backgroundColor: 'rgb(0,0,0)' }}
      />
      <div
        className="mx-9 border-t border-dashed"
        style={{ borderColor: 'rgba(255,255,255,0.14)' }}
      />
    </div>
  );
}

function Fact({
  label,
  value,
  sub,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-label-3">
        {label}
      </p>
      <p
        className={
          'mt-1.5 text-[15px] font-medium text-label ' + (mono ? 'font-mono' : '')
        }
      >
        {value}
      </p>
      {sub && <p className="mt-0.5 text-[12px] text-label-2">{sub}</p>}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div
      className="h-[420px] animate-pulse rounded-[28px] border"
      style={{
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderColor: 'rgba(255,255,255,0.06)',
      }}
    />
  );
}

function ErrorCard({
  error,
  code,
  onRetry,
}: {
  error: ApiCallError;
  code: string;
  onRetry: () => void;
}) {
  const headline =
    error.code === 'not_found'
      ? 'Lock не найден'
      : error.code === 'invalid_code_format'
        ? 'Неверный формат кода'
        : 'Не удалось загрузить';

  const detail =
    error.code === 'not_found'
      ? `Кода ${code} нет в системе. Проверьте написание — формат AS-XXXX-YYYY.`
      : error.code === 'invalid_code_format'
        ? 'Код должен быть формата AS-XXXX-YYYY (заглавные буквы и цифры).'
        : error.message;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="rounded-[28px] border p-7"
      style={{
        backgroundColor: 'rgba(255,69,58,0.06)',
        borderColor: 'rgba(255,69,58,0.20)',
      }}
    >
      <p className="text-sm font-medium text-red-400">{headline}</p>
      <p className="mt-2 text-sm text-label-2">{detail}</p>
      {error.code !== 'not_found' && error.code !== 'invalid_code_format' && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 rounded-full px-4 py-2 text-sm font-medium text-label transition-colors"
          style={{ backgroundColor: 'rgba(255,255,255,0.10)' }}
        >
          Повторить
        </button>
      )}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Live countdown — refreshes every 30s alongside the query
// ---------------------------------------------------------------------------

function Countdown({ to }: { to: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const target = new Date(to).getTime();
  const ms = target - now;
  if (ms <= 0) return <span className="text-red-400">истёк</span>;
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  if (days > 0) return <>осталось {days} дн.</>;
  if (hours > 0) return <>осталось {hours} ч.</>;
  return <>менее часа</>;
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatRubles(kopecks: number): string {
  const rub = Math.round(kopecks / 100);
  return new Intl.NumberFormat('ru-RU').format(rub) + ' ₽';
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(iso));
}

function formatTime(iso: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}
